/**
 * PushNotificationBanner — iter 264b.
 *
 * A dismissible header banner that prompts users to grant browser notification
 * permission when it's currently in the "default" (never-asked) state. Auto-
 * hides after the user makes a choice OR clicks "Not now" (dismissal is
 * session-scoped in sessionStorage so the banner reappears next visit if the
 * user still hasn't decided).
 *
 * On "Enable", triggers Notification.requestPermission() and — if granted —
 * subscribes the browser to push via the /api/notifications/subscribe endpoint
 * so the user immediately starts receiving driver-dispatch alerts.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';

const DISMISS_KEY = 'drayly_push_prompt_dismissed';

const _urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

export const PushNotificationBanner = ({ appType = 'dispatch' }) => {
  const { api, user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.user_id) return;
    if (!('PushManager' in window) || !('serviceWorker' in navigator) || !('Notification' in window)) return;

    // Only prompt when we've truly never asked. If the user already denied,
    // browsers block us from re-prompting programmatically — show a link to
    // the browser's site settings instead. For simplicity we only show the
    // banner in the "default" state.
    if (Notification.permission !== 'default') return;

    if (sessionStorage.getItem(DISMISS_KEY) === '1') return;

    setVisible(true);
  }, [user?.user_id]);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  }, []);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        // User denied or dismissed — hide banner for the session either way.
        dismiss();
        if (perm === 'denied') {
          toast.info('Notifications blocked. You can re-enable them from your browser settings.');
        }
        return;
      }

      // Permission granted — subscribe now so the first dispatch alert works.
      const reg = await navigator.serviceWorker.ready;
      let subscription = await reg.pushManager.getSubscription();

      if (!subscription) {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/vapid-public-key`);
        const { publicKey } = await res.json();
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: _urlBase64ToUint8Array(publicKey),
        });
      }

      await api.post('/notifications/subscribe', {
        subscription: subscription.toJSON(),
        app_type: appType,
      });

      toast.success('Notifications enabled — you\'ll get instant dispatch alerts.');
      setVisible(false);
    } catch (e) {
      console.error('[Push banner] enable failed:', e);
      toast.error('Could not enable notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [api, appType, dismiss]);

  if (!visible) return null;

  return (
    <div
      className="w-full bg-gradient-to-r from-blue-600/95 to-indigo-600/95 border-b border-blue-500/50 px-4 py-2 flex items-center justify-between gap-3 text-sm text-white"
      data-testid="push-notification-banner"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Bell className="h-4 w-4 flex-shrink-0" />
        <span className="truncate">
          Get instant driver dispatch alerts — enable notifications so you never miss a load update.
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          onClick={enable}
          disabled={busy}
          className="h-7 bg-white/90 hover:bg-white text-blue-700 hover:text-blue-800 text-xs px-3"
          data-testid="push-banner-enable-btn"
        >
          {busy ? 'Enabling…' : 'Enable'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={dismiss}
          className="h-7 text-white/80 hover:text-white hover:bg-white/10 text-xs px-2"
          data-testid="push-banner-dismiss-btn"
          aria-label="Dismiss notification prompt"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default PushNotificationBanner;
